import { apiFetch, v1Path } from './api';
import type { ProgressCounts, StateDto, WorkItemListDecorations, WorkItemSlim } from './work-types';

export type LaunchStepRow = WorkItemSlim & WorkItemListDecorations;

export type LaunchLive = { id: string; live_at: string | null; live_by_id: string | null; version: number };

/** Launch steps are the top-level tasks linked straight to an initiative. */
export function launchSteps<T extends { type: string; initiative_id?: string | null; parent_id: string | null }>(
  rows: T[],
  initiativeId: string,
): T[] {
  return rows.filter((row) => row.parent_id === null && row.initiative_id === initiativeId && row.type === 'task');
}

/** Dated steps by go-live date, then the rest. */
export function sortLaunchSteps<T extends { due_date: string | null; start_date: string | null; key: string }>(rows: T[]): T[] {
  return [...rows].sort((left, right) => {
    if (left.due_date && right.due_date && left.due_date !== right.due_date) return left.due_date < right.due_date ? -1 : 1;
    if (left.due_date && !right.due_date) return -1;
    if (!left.due_date && right.due_date) return 1;
    return left.key.localeCompare(right.key, undefined, { numeric: true });
  });
}

/** A task counts its subtasks, or itself when it has none. */
export function stepProgress(row: LaunchStepRow, state: StateDto | undefined): ProgressCounts {
  if (row.rollup && row.rollup.total > 0) return row.rollup;
  const done = state?.group === 'completed' ? 1 : 0;
  return { total: 1, done, started: state?.group === 'started' ? 1 : 0, cancelled: 0 };
}

export function isStepDone(state: StateDto | undefined): boolean {
  return state?.group === 'completed';
}

export function setLaunchLive(initiativeId: string, workItemId: string, live: boolean): Promise<LaunchLive> {
  return apiFetch<LaunchLive>(v1Path(`/initiatives/${initiativeId}/steps/${workItemId}/live`), {
    method: live ? 'POST' : 'DELETE',
  });
}

/** Steps only reach the viewer from streams they belong to, which is the server's rule for marking live. */
export function canMarkLaunchLive(session: { userId: string | null }): boolean {
  return Boolean(session.userId);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days from `today` to a YYYY-MM-DD date; negative once it has passed. */
export function daysUntil(dateOnly: string, today: Date): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly);
  if (!match) return null;
  const target = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((target.getTime() - start.getTime()) / DAY_MS);
}

export function launchCountdown(dueDate: string | null, settled: boolean, today: Date): string {
  if (!dueDate) return 'No launch date set';
  if (settled) return 'Launched';
  const days = daysUntil(dueDate, today);
  if (days === null) return '';
  if (days === 0) return 'Launches today';
  if (days > 0) return `${days} ${days === 1 ? 'day' : 'days'} remaining`;
  return `${-days} ${days === -1 ? 'day' : 'days'} overdue`;
}
